var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Flex, Grid } from '@chakra-ui/react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Button, FormButton, GenericError, HeadingText, Markdown, MediaImage, ModalBase, PageWrapper, PlainText, renderInputBlock, renderLayoutBlock } from '@/components';
import { NavigationMenu } from '@/components/layout/NavigationMenu';
import { isClinro } from '@/config';
import { useBroadcastClinro, useCachedQueries, usePreloader, usePrompt, useQuestionnaireForm, useResolveValue } from '@/hooks';
import { CaptureService } from '@/services';
import { addPageAnswers, closeLoading, loadPartialSubmission, resetPartialSubmission, resetQuestionnaire, setCurrentRoute, setFullState, setQuestionnaire, setUserPhaseEventDone, showLoading, useAuth, useQuestionnaire, useUI } from '@/store';
import { APP_CONFIG, AuthActionType, BlockWithMultipleAnswer, BroadcastChannelType, DataTest, LayoutBlockType, MessageType, NativeAction, ROUTE, StaticBlockType, SubmissionPartialsType, UrlParamsKey, UserPhaseEventMetaType } from '@/types';
import { ResolveDate, ResolveQuestionnaireExitAction, asyncTimeout, buildSceneKey, emitNativeWebView, generateRoute, getSectionForCurrentPosition, isMediaValid } from '@/utils';
import { addQbMocks, handleQbReset } from '@/utils/qbPreview';
const resolveDate = new ResolveDate();
const resolveQuestionnaireExitAction = new ResolveQuestionnaireExitAction();
export const Questionnaire = () => {
    var _a;
    const { t } = useTranslation();
    const [resolver] = useResolveValue();
    const [searchParams] = useSearchParams();
    const params = useParams();
    const navigate = useNavigate();
    const uiCtx = useUI();
    const questCtx = useQuestionnaire();
    const [coreMediaError, setCoreMediaError] = useState(false);
    const [startupError, setStartupError] = useState(null);
    const latestStateRef = useRef(questCtx.state);
    useEffect(() => {
        latestStateRef.current = questCtx.state;
    }, [questCtx.state]);
    useBroadcastClinro();
    const { dispatch: authDispatch, isLoggedIn, state: { currentRoute, currentUserPhaseEvent, currentUserPhaseSegment, schedule, user } } = useAuth();
    const { blockList, getAnswersDependency, mapAnswerExitActions, mapBlockAnswersList, mapBlockInputByScene, state: { current, partialSubmission, questionnaire: { core_texts, exit_page, post_submission_scenes, sections, settings, submission_partials, title } } } = questCtx;
    const [loading, setLoading] = useState(false);
    const [isPostSubmission, setIsPostSubmission] = useState(false);
    const [confirmIndex, setConfirmIndex] = useState(null);
    const [currentScene, { canGoToNextPage, canGoToPrevPage, canSubmitQuestionnaire, getCurrentAnswers, goToNextPage, goToPrevPage, goToSection, handleLoadPartial, isNavigationLocked, mostForwardSectionKey, pageConfirmations, pageErrors, pageIsValid, queue, setNavigationLock, updateMostForwardSection }] = useQuestionnaireForm(handleSubmit, handleSubmitPartial, setLoading);
    const [imagesPreloaded, { isPreloadFailed, preloadImages }] = usePreloader();
    const currentConfirmation = pageConfirmations[confirmIndex];
    const answersExist = current.state.answers.length > 0;
    // A warning prompt is shown when navigate away from the page when,
    // - the user has answered at least one question
    // - the page is not loading
    // - the questionnaire has not been submitted i.e. they are on post submission scenes
    // - there is no partial submission enabled (automatic saving on navigation)
    const shouldShowPrompt = answersExist && !loading && !isPostSubmission && !submission_partials;
    const { cancelNavigation, confirmNavigation, showPrompt } = usePrompt(shouldShowPrompt);
    const ids = useMemo(() => {
        var _a;
        const questionnaire_id = params === null || params === void 0 ? void 0 : params.id;
        return {
            event_id: currentUserPhaseEvent.id ||
                searchParams.get(UrlParamsKey.EVENT_ID) ||
                ((_a = schedule.find((event) => event.meta.some((meta) => meta.key === UserPhaseEventMetaType.QUESTIONNAIRE_ID &&
                    meta.value === questionnaire_id))) === null || _a === void 0 ? void 0 : _a.id),
            questionnaire_id,
            submitted_by: (user === null || user === void 0 ? void 0 : user.data.id) || searchParams.get(UrlParamsKey.SUBMITTED_BY),
            submitted_for: (user === null || user === void 0 ? void 0 : user.data.id) || searchParams.get(UrlParamsKey.SUBMITTED_FOR)
        };
    }, [params, searchParams, currentUserPhaseEvent === null || currentUserPhaseEvent === void 0 ? void 0 : currentUserPhaseEvent.id, user === null || user === void 0 ? void 0 : user.data.id]);
    // Listen for auth changes in Clinro mode and close questionnaire if user changed
    useEffect(() => {
        if (!isClinro)
            return;
        const channel = new BroadcastChannel(BroadcastChannelType.CLINRO_AUTH);
        const handleAuthChange = (event) => {
            const { type, userUuid } = event.data;
            // Close questionnaire if:
            // 1. User logged out, OR
            // 2. Different user logged in (userUuid doesn't match submitted_by)
            if (type === 'logout' || (type === 'login' && userUuid !== ids.submitted_by)) {
                self.close();
            }
        };
        channel.addEventListener('message', handleAuthChange);
        return () => {
            channel.removeEventListener('message', handleAuthChange);
            channel.close();
        };
    }, [ids.submitted_by]);
    const eventType = useMemo(() => {
        var _a;
        return (searchParams.get(UrlParamsKey.EVENT_TYPE) ||
            ((_a = schedule.find((event) => event.id === ids.event_id)) === null || _a === void 0 ? void 0 : _a.type) ||
            currentUserPhaseEvent.type);
    }, [schedule, ids.event_id]);
    const clinroMetaParams = {
        segment_originator_id: searchParams.get(UrlParamsKey.SEGMENT_ORIGINATOR_ID),
        segment_originator_type: searchParams.get(UrlParamsKey.SEGMENT_ORIGINATOR_TYPE)
    };
    // Specific params coming from Change Request page/Clinro
    const changeReqParams = {
        entry_mode: searchParams.get(UrlParamsKey.ENTRY_MODE),
        started_at: searchParams.get(UrlParamsKey.STARTED_AT),
        submitted_at: searchParams.get(UrlParamsKey.SUBMITTED_AT),
        submitted_at_timezone: searchParams.get(UrlParamsKey.SUBMITTED_AT_TIMEZONE)
    };
    const startedAtOverride = useMemo(() => {
        var _a;
        const startedAt = (_a = changeReqParams.started_at) === null || _a === void 0 ? void 0 : _a.trim();
        return startedAt || undefined;
    }, [changeReqParams.started_at]);
    const { handleCachedQueries } = useCachedQueries({
        now_override: changeReqParams === null || changeReqParams === void 0 ? void 0 : changeReqParams.submitted_at,
        participant_id: ids.submitted_for,
        questionnaire_id: ids.questionnaire_id,
        user_event_id: ids.event_id
    });
    // PREVIEW QUESTIONNAIRES
    useEffect(() => {
        const handleMessage = (event) => {
            var _a;
            if (!Object.values(MessageType).includes(event.data.type)) {
                return;
            }
            if (event.data.type === MessageType.QB_PREVIEW) {
                const questionnaireId = (_a = latestStateRef.current.questionnaire) === null || _a === void 0 ? void 0 : _a.id;
                // If the questionnaire is not loaded, set the questionnaire state
                // Otherwise, update the questionnaire state with the preview questionnaire
                if (!questionnaireId) {
                    const payload = Object.assign({ nowOverride: changeReqParams === null || changeReqParams === void 0 ? void 0 : changeReqParams.submitted_at, partialSubmission: null, questionnaire: JSON.parse(event.data.data) }, (startedAtOverride && { startedAtOverride }));
                    questCtx.dispatch(setQuestionnaire(payload));
                }
                else {
                    const previewQuestionnaire = JSON.parse(event.data.data);
                    const latestState = latestStateRef.current;
                    const payload = Object.assign({}, latestState);
                    latestState.questionnaire = previewQuestionnaire;
                    questCtx.dispatch(setFullState(payload));
                }
            }
            if (event.data.type === MessageType.QB_RESET) {
                handleQbReset(questCtx, event.data);
            }
            addQbMocks(questCtx, event.data);
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);
    useEffect(() => {
        if (!isMediaValid(questCtx)) {
            uiCtx.dispatch(closeLoading());
            setCoreMediaError(true);
        }
    }, [blockList]);
    useEffect(() => {
        return () => {
            if (isPostSubmission) {
                resolveQuestionnaireExitAction.executeActions();
            }
        };
    }, [isPostSubmission]);
    // Reset questionnaire state when component unmounts
    useEffect(() => {
        return () => {
            questCtx.dispatch(resetQuestionnaire());
        };
    }, []);
    useEffect(() => {
        const checkAndLoad = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!isClinro && !isLoggedIn)
                return;
            uiCtx.dispatch(showLoading());
            if ((eventType === null || eventType === void 0 ? void 0 : eventType.toUpperCase()) === APP_CONFIG.COMPONENTS.ScheduleEventType.QUESTIONNAIRE) {
                try {
                    const response = yield CaptureService.eventSubmissionCheck({
                        participant_id: isClinro ? ids.submitted_for : undefined,
                        user_event_id: ids.event_id
                    });
                    if (response === 204) {
                        authDispatch({
                            payload: { completedAt: new Date(), eventId: ids.event_id },
                            type: AuthActionType.SET_COMPLETED_USER_PHASE_EVENTS
                        });
                        if (isClinro) {
                            const path = generateRoute(ROUTE.CLINRO_PARTICIPANT_SCHEDULE_EVENTS, {
                                participantId: ids.submitted_for
                            });
                            window.location.href = `${window.location.origin}${path}`;
                        }
                        else {
                            navigate(ROUTE.DASHBOARD);
                        }
                        return;
                    }
                }
                catch (err) {
                    console.error('Error during eventSubmissionCheck', err);
                }
            }
            yield getQuestionnaire();
        });
        checkAndLoad();
    }, [isClinro, isLoggedIn]);
    const getQuestionnaire = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!ids.event_id) {
                setStartupError(new Error('schedule event_id is missing'));
                return;
            }
            const questionnaire = yield CaptureService.getQuestionnaire(ids);
            let partialSubmission = null;
            if (questionnaire === null || questionnaire === void 0 ? void 0 : questionnaire.submission_partials) {
                partialSubmission = yield CaptureService.getPartialSubmission(ids.event_id);
            }
            yield preloadImages(questionnaire.core_media);
            yield handleCachedQueries(questionnaire.cached_queries);
            resolveQuestionnaireExitAction.actions = questionnaire.exit_actions;
            questCtx.dispatch(setQuestionnaire(Object.assign({ nowOverride: changeReqParams === null || changeReqParams === void 0 ? void 0 : changeReqParams.submitted_at, partialSubmission,
                questionnaire }, (startedAtOverride && { startedAtOverride }))));
        }
        finally {
            uiCtx.dispatch(closeLoading());
        }
    });
    const loadPartial = () => {
        questCtx.dispatch(loadPartialSubmission());
        handleLoadPartial();
        handleResetPartial();
    };
    const handleResetPartial = () => {
        questCtx.dispatch(resetPartialSubmission());
    };
    const handleNextPage = () => {
        if (pageConfirmations.length) {
            setConfirmIndex(0);
        }
        else {
            goToNextPage();
        }
    };
    const handleCancelConfirmation = () => {
        setConfirmIndex(null);
    };
    const handleConfirmConfirmation = () => {
        const nextConfirmationIndex = confirmIndex + 1;
        if (nextConfirmationIndex < pageConfirmations.length) {
            setConfirmIndex(nextConfirmationIndex);
        }
        else {
            setConfirmIndex(null);
            goToNextPage();
        }
    };
    const handleNavigation = (delay = false) => __awaiter(void 0, void 0, void 0, function* () {
        if (isClinro) {
            if (changeReqParams === null || changeReqParams === void 0 ? void 0 : changeReqParams.submitted_at) {
                self.close();
            }
            navigate(ROUTE.LENS_SUBMITTED);
            return;
        }
        if (delay) {
            yield asyncTimeout(() => {
                navigate(ROUTE.DASHBOARD);
            });
            return;
        }
        navigate(ROUTE.DASHBOARD);
    });
    const handleCallToActionClick = () => {
        if (isNavigationLocked)
            return;
        if (!canGoToNextPage) {
            resolveQuestionnaireExitAction.executeActions();
            handleNavigation();
        }
        goToNextPage();
    };
    const handleSubmissionPayload = () => {
        var _a, _b;
        // Set of all scenes by key
        const scenesKeySet = new Set(queue.itemsArray.map((page) => buildSceneKey(page)));
        // Map of all possible answers from the questionnaire
        const allBlocksAnswersMap = mapBlockAnswersList;
        // All block's that are in the journey
        const blocksInJourney = new Set();
        scenesKeySet.forEach((key) => {
            const blockList = mapBlockInputByScene.get(key);
            if (blockList) {
                blockList.forEach((block) => {
                    return blocksInJourney.add(block.id);
                });
            }
        });
        const finalAnswers = [];
        const finalExitActionQueue = Object.entries(queue.history).map(([scene_key, itemList]) => {
            return {
                actioned_by: { repeat_key: null, scene_key },
                queue: Object.values(itemList)
            };
        });
        const finalJourney = queue.itemsArray.map(({ repeat_index, repeat_key, scene_key }) => ({
            repeat_index,
            repeat_key,
            scene_key
        }));
        // Add all answers that are in the journey
        finalJourney.forEach((page) => {
            // Get all blocks for a given scene
            const blocks = mapBlockInputByScene.get(page.scene_key);
            // For each block, get all answers, update the page key and add to final answers. If the
            // answer is already answered by the user, use the user's answer. Otherwise, use the base answer.
            blocks.forEach((block) => {
                const answersListMap = allBlocksAnswersMap.get(block.id);
                const baseAnswersArray = Array.from(answersListMap.values());
                const updatedBaseAnswers = baseAnswersArray.map((baseAnswer) => {
                    var _a, _b;
                    const answeredAnswer = current.state.answers.find((a) => a.answer_id === baseAnswer.answer_id &&
                        a.answer_key === baseAnswer.answer_key &&
                        a.page_key.scene_key === page.scene_key &&
                        (a.page_key.repeat_key === page.repeat_key || a.page_key.repeat_key === null) &&
                        (a.page_key.repeat_index === page.repeat_index || a.page_key.repeat_index === null));
                    const newAnswer = answeredAnswer ? answeredAnswer : baseAnswer;
                    return Object.assign(Object.assign({}, newAnswer), { page_key: {
                            repeat_index: (_a = page.repeat_index) !== null && _a !== void 0 ? _a : null,
                            repeat_key: (_b = page.repeat_key) !== null && _b !== void 0 ? _b : null,
                            scene_key: page.scene_key
                        } });
                });
                finalAnswers.push(...updatedBaseAnswers.values());
            });
        });
        // Remove meta from final answers
        finalAnswers.forEach((a) => {
            // biome-ignore lint/performance/noDelete: Delete is required
            a === null || a === void 0 ? true : delete a.meta;
            a.id = uuidv4();
        });
        const finalComputedValues = (_a = current.computed_values) === null || _a === void 0 ? void 0 : _a.filter(({ page_key }) => {
            const key = buildSceneKey(page_key);
            return scenesKeySet.has(key);
        });
        const finalStateComputedValues = (_b = current.computed_values) === null || _b === void 0 ? void 0 : _b.map(({ computed_value_key, page_key, typed_value }) => ({
            computed_value_key,
            page_key,
            typed_value
        }));
        // If isClinro is enabled use clinroMeta values
        // otherwise, use currentUserPhaseSegment values
        const segment_originator_id = isClinro
            ? clinroMetaParams.segment_originator_id
            : currentUserPhaseSegment.originator.id;
        const segment_originator_type = isClinro
            ? clinroMetaParams.segment_originator_type
            : currentUserPhaseSegment.originator.type;
        const event_type = eventType === null || eventType === void 0 ? void 0 : eventType.toLowerCase();
        return Object.assign(Object.assign(Object.assign({}, current), { answers: finalAnswers, computed_values: finalComputedValues, meta: Object.assign(Object.assign({}, current.meta), { event_type: event_type, segment_originator_id,
                segment_originator_type }), state: Object.assign(Object.assign({}, current.state), { answers: current.state.answers.map(({ answer_key, block_key, page_key, typed_value }) => ({
                    answer_key,
                    block_key,
                    page_key,
                    typed_value
                })), computed_values: finalStateComputedValues, exit_action_queue_history: finalExitActionQueue, journey: finalJourney }), submitted_at: (changeReqParams === null || changeReqParams === void 0 ? void 0 : changeReqParams.submitted_at) || resolveDate.formatDate(new Date()).tz_offset, webQueue: {
                history: queue.history,
                items: queue.items,
                position: queue.position
            } }), ids);
    };
    function handleSubmitPartial() {
        return __awaiter(this, void 0, void 0, function* () {
            const partial = handleSubmissionPayload();
            if (queue.state.current.scene_key !== exit_page.scene_key) {
                partial.state.journey.splice(queue.position + 1);
            }
            const payload = Object.assign(Object.assign({ type: SubmissionPartialsType.LAST_ACTIVITY }, submission_partials), { current_timestamp: new Date().getTime(), partial, questionnaire_started_at: current.meta.started_at });
            return CaptureService.submitPartialQuestionnaire(payload);
        });
    }
    function handleSubmit() {
        return __awaiter(this, void 0, void 0, function* () {
            // biome-ignore lint/suspicious/noAsyncPromiseExecutor: Keep async promise executor as changing it modifies
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    setLoading(true);
                    const payload = { submissions: [handleSubmissionPayload()] };
                    if (isClinro && (changeReqParams === null || changeReqParams === void 0 ? void 0 : changeReqParams.submitted_at)) {
                        const channel = new BroadcastChannel(BroadcastChannelType.CLINRO);
                        channel.postMessage({ payload, type: 'change-request-submitted' });
                    }
                    else {
                        yield CaptureService.submitQuestionnaire(payload);
                        if (eventType.toUpperCase() === APP_CONFIG.COMPONENTS.ScheduleEventType.QUESTIONNAIRE) {
                            authDispatch({
                                payload: { completedAt: new Date(), eventId: ids.event_id },
                                type: AuthActionType.SET_COMPLETED_USER_PHASE_EVENTS
                            });
                        }
                        authDispatch(setUserPhaseEventDone(ids.questionnaire_id));
                        emitNativeWebView({ type: NativeAction.GO_BACK });
                    }
                    const hasPostSubmissionScenes = post_submission_scenes.length > 0;
                    if (!canGoToNextPage && !hasPostSubmissionScenes) {
                        resolveQuestionnaireExitAction.executeActions();
                        yield handleNavigation(true);
                    }
                    setIsPostSubmission(true);
                    resolve(true);
                }
                catch (e) {
                    reject();
                }
                finally {
                    setLoading(false);
                }
            }));
        });
    }
    const handleChange = (newAnswer, options = {}) => {
        const blockKey = (Array.isArray(newAnswer) ? newAnswer[0] : newAnswer).block_key;
        const answersDependency = getAnswersDependency(blockKey, queue.state.current);
        const removedAnswers = resolver.resolveDependency(answersDependency);
        const payload = {
            newAnswer,
            options: Object.assign(Object.assign({}, options), { repeatIndex: queue.state.current.repeat_index, repeatKey: queue.state.current.repeat_key, sceneKey: currentScene.key }),
            removedAnswers
        };
        // If the block is referenced by an exit action and we are using sections,
        // update the most forward section to the current scene
        if ((sections === null || sections === void 0 ? void 0 : sections.length) > 0) {
            const currentSceneActions = mapAnswerExitActions.get(currentScene.key);
            currentSceneActions === null || currentSceneActions === void 0 ? void 0 : currentSceneActions.forEach((action) => {
                if (action.block_key === blockKey) {
                    const currentSection = getSectionForCurrentPosition(queue.itemsArray, sections, queue.position);
                    updateMostForwardSection(currentSection.scene_key);
                }
            });
        }
        questCtx.dispatch(addPageAnswers(payload));
    };
    useEffect(() => {
        authDispatch(setCurrentRoute(Object.assign(Object.assign({}, currentRoute), { title })));
    }, [title, currentRoute.title]); // Use currentRoute.title as reactive dependency as AuthContext can override the title in some cases
    const renderBlock = (block) => {
        var _a, _b, _c;
        if ('key' in block) {
            const answers = getCurrentAnswers(block.key);
            const defaultValue = BlockWithMultipleAnswer.includes(block.type)
                ? answers.map((x) => x.value)
                : (_a = answers === null || answers === void 0 ? void 0 : answers[0]) === null || _a === void 0 ? void 0 : _a.value;
            const answerKey = BlockWithMultipleAnswer.includes(block.type)
                ? answers.map((x) => x.answerKey)
                : (_b = answers === null || answers === void 0 ? void 0 : answers[0]) === null || _b === void 0 ? void 0 : _b.answerKey;
            const errors = pageErrors.get(block.key) || [];
            if ('answer_filters' in block) {
                const answersKey = resolver.resolveAnswerFilter(block.answer_filters);
                if (answersKey.length) {
                    (_c = mapBlockAnswersList.get(block.id)) === null || _c === void 0 ? void 0 : _c.forEach((answer) => {
                        // biome-ignore lint/performance/noDelete: <explanation>
                        delete answer.meta.filtered;
                        if (!answersKey.includes(answer.answer_key)) {
                            answer.meta.filtered = true;
                        }
                    });
                }
            }
            return renderInputBlock({
                answerKey,
                block,
                // @ts-ignore - The expected type comes from property 'defaultValue' which is declared here on type 'InputBlockComponentProps'
                defaultValue,
                errors,
                onChange: handleChange,
                onNavigationLockChange: setNavigationLock,
                options: {
                    participant_id: ids.submitted_for,
                    questionnaire_id: ids.questionnaire_id,
                    submitted_by: ids.submitted_by
                },
                settings
            });
        }
        if (block.type === LayoutBlockType.LAYOUT_CONTAINER) {
            return renderLayoutBlock({ block, renderBlock });
        }
        return renderStaticBlock(block);
    };
    const renderStaticBlock = (block) => {
        switch (block.type) {
            case StaticBlockType.STATIC_HEADING:
                return _jsx(HeadingText, { block: block });
            case StaticBlockType.STATIC_MARKDOWN:
            case StaticBlockType.STATIC_MARKDOWN_INSTRUCTION:
                return _jsx(Markdown, { block: block });
            case StaticBlockType.STATIC_MEDIA_IMAGE:
                return (_jsx(MediaImage, { block: Object.assign(Object.assign({}, block), { media: Object.assign(Object.assign({}, block.media), (imagesPreloaded[block.media.key] || {})) }) }));
            case StaticBlockType.STATIC_TEXT:
                return _jsx(PlainText, { block: block });
            default:
                return _jsx(_Fragment, {});
        }
    };
    if (startupError)
        throw startupError;
    if (!currentScene)
        return null;
    let navigationControls = null;
    if ('button_text' in currentScene) {
        navigationControls = (_jsx(Button, Object.assign({ "data-test": DataTest.QUESTIONNAIRE_CTA, disabled: isNavigationLocked, onClick: handleCallToActionClick, size: 'lg' }, { children: currentScene.button_text })));
    }
    else {
        navigationControls = (_jsxs(_Fragment, { children: [canGoToPrevPage && (_jsx(Button, Object.assign({ "data-test": DataTest.QUESTIONNAIRE_NAV_BACKWARD, disabled: isNavigationLocked, onClick: goToPrevPage, variant: 'outline' }, { children: core_texts.nav_back }))), _jsx(FormButton, Object.assign({ "data-test": DataTest.QUESTIONNAIRE_NAV_FORWARD, disabled: !pageIsValid || isNavigationLocked, loading: loading, onClick: handleNextPage, size: 'md', type: 'submit' }, { children: canSubmitQuestionnaire
                        ? core_texts.nav_submit
                        : canGoToPrevPage
                            ? core_texts.nav_continue
                            : core_texts.nav_start }))] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(ModalBase, Object.assign({ footer: _jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ onClick: handleConfirmConfirmation }, { children: currentConfirmation === null || currentConfirmation === void 0 ? void 0 : currentConfirmation.confirm_text })), _jsx(Button, Object.assign({ onClick: handleCancelConfirmation, variant: 'outline' }, { children: currentConfirmation === null || currentConfirmation === void 0 ? void 0 : currentConfirmation.cancel_text }))] }), isOpen: Number.isInteger(confirmIndex), title: currentConfirmation === null || currentConfirmation === void 0 ? void 0 : currentConfirmation.title }, { children: currentConfirmation === null || currentConfirmation === void 0 ? void 0 : currentConfirmation.content })), _jsx(ModalBase, Object.assign({ footer: _jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ "data-test": DataTest.QUESTIONNAIRE_LOAD_PARTIAL_MODAL_NO, onClick: handleResetPartial, variant: 'outline' }, { children: t('app:components.common.partial_submission.dialog.no_text') })), _jsx(Button, Object.assign({ "data-test": DataTest.QUESTIONNAIRE_LOAD_PARTIAL_MODAL_YES, onClick: loadPartial }, { children: t('app:components.common.partial_submission.dialog.yes_text') }))] }), isOpen: Boolean(submission_partials && (partialSubmission === null || partialSubmission === void 0 ? void 0 : partialSubmission.partial)), title: title }, { children: t('app:components.common.partial_submission.dialog.body') })), _jsx(ModalBase, Object.assign({ footer: _jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ onClick: cancelNavigation, variant: 'outline' }, { children: core_texts.quit_prompt_cancel })), _jsx(Button, Object.assign({ onClick: confirmNavigation, variant: 'cancel' }, { children: core_texts.quit_prompt_quit }))] }), isOpen: showPrompt, onClose: cancelNavigation, title: core_texts.quit_prompt_title }, { children: core_texts.quit_prompt_content })), _jsxs(Flex, Object.assign({ style: { flexDirection: 'row' } }, { children: [(sections === null || sections === void 0 ? void 0 : sections.length) > 0 && !isPostSubmission && (_jsx("div", { children: _jsx(NavigationMenu, { className: isNavigationLocked ? 'disabled' : '', goToSection: goToSection, mostForwardSectionKey: mostForwardSectionKey, queue: queue, sections: sections }) })), _jsx(PageWrapper, Object.assign({ color: 'darkBrandBlue.90', py: '8', title: title }, { children: isPreloadFailed || coreMediaError ? (_jsx(GenericError, {})) : (_jsxs(Flex, Object.assign({ direction: 'column', gap: '16', h: 'inherit' }, { children: [_jsx(Flex, Object.assign({ direction: 'column', fontSize: 'md' }, { children: (currentScene.main_blocks || []).map((block) => (_jsx(Fragment, { children: renderBlock(block) }, block.id))) })), _jsxs(Flex, Object.assign({ direction: 'column', gap: '16', mt: 'auto', w: '100%' }, { children: [((_a = currentScene === null || currentScene === void 0 ? void 0 : currentScene.footer_blocks) === null || _a === void 0 ? void 0 : _a.length) > 0 && (_jsx(Flex, Object.assign({ direction: 'column', fontSize: 'md' }, { children: currentScene.footer_blocks.map((block) => (_jsx(Fragment, { children: renderBlock(block) }, block.id))) }))), _jsx(Grid, Object.assign({ gap: '5', gridAutoColumns: 'minmax(0, 1fr)', gridAutoFlow: 'column' }, { children: navigationControls }))] }))] }))) }))] }))] }));
};
